import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Calendar } from "lucide-react";
import api, { resolveImg } from "../lib/api";

const CATEGORIES = [
  { id: "all", name: "All Articles" },
  { id: "beginner-guides", name: "Beginner Guides" },
  { id: "safety", name: "Safety" },
  { id: "education", name: "Education" },
  { id: "our-story", name: "Our Story" },
];

export default function Blog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const category = searchParams.get("category") || "all";

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    setLoading(true);
    const params = {};
    if (category !== "all") params.category = category;
    api
      .get("/blog", { params })
      .then((r) => setPosts(r.data))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, [category]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const setCategory = (id) => {
    const np = new URLSearchParams(searchParams);
    if (id === "all") np.delete("category");
    else np.set("category", id);
    setSearchParams(np);
  };

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-12 lg:py-20" data-testid="blog-page">
      <div className="mb-12 max-w-3xl">
        <p className="eyebrow mb-3">From the journal</p>
        <h1 className="font-serif text-5xl lg:text-6xl text-[var(--ink-forest)] tracking-tight">
          Slow reads &
          <span className="italic text-[var(--terracotta)]"> small wisdoms.</span>
        </h1>
        <p className="mt-5 text-[var(--muted-fg)] leading-relaxed text-lg">
          Beginner guides, safety notes, sourcing stories, and gentle reminders — written for the
          curious and the careful.
        </p>
      </div>

      <div className="flex gap-2 flex-wrap mb-10" data-testid="blog-categories">
        {CATEGORIES.map((c) => (
          <button
            key={c.id}
            onClick={() => setCategory(c.id)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              category === c.id
                ? "bg-[var(--terracotta)] text-white"
                : "bg-[var(--bg-surface)] border border-[var(--border)] text-[var(--ink-forest)] hover:border-[var(--terracotta)]"
            }`}
            data-testid={`blog-cat-${c.id}`}
          >
            {c.name}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="aspect-[4/5] bg-[var(--muted)] rounded-2xl animate-pulse" />
          ))}
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center py-20">
          <p className="font-serif text-2xl text-[var(--ink-forest)]">No articles yet.</p>
          <p className="mt-2 text-[var(--muted-fg)]">Try another category.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7 lg:gap-8">
          {posts.map((p) => (
            <BlogCard key={p.id} post={p} />
          ))}
        </div>
      )}
    </div>
  );
}

export function BlogCard({ post }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group block"
      data-testid={`blog-card-${post.slug}`}
    >
      <div className="aspect-[4/3] overflow-hidden rounded-2xl bg-[var(--muted)]">
        <img
          src={resolveImg(post.cover_image)}
          alt={post.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          loading="lazy"
        />
      </div>
      <div className="pt-5">
        <p className="eyebrow text-[10px]">{categoryLabel(post.category)}</p>
        <h3 className="mt-2 font-serif text-2xl text-[var(--ink-forest)] group-hover:text-[var(--terracotta)] transition-colors leading-tight">
          {post.title}
        </h3>
        <p className="mt-3 text-sm text-[var(--muted-fg)] line-clamp-3 leading-relaxed">
          {post.excerpt}
        </p>
        <p className="mt-4 inline-flex items-center gap-1.5 text-xs text-[var(--muted-fg)]">
          <Calendar className="w-3 h-3" />
          {new Date(post.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
        </p>
      </div>
    </Link>
  );
}

function categoryLabel(c) {
  const cat = CATEGORIES.find((x) => x.id === c);
  return cat ? cat.name : c;
}
